/**
 * Native ATD ("approved to dispense"), as /api/v1/atd answers it.
 *
 * eMerge owns the word; Monday's baseline is only the first history row. The
 * server decides which moves a caller may make (`allowed_for_you`); this file
 * names shapes and sends the act, nothing more. A refusal is
 * `{ success:false, code, message }` and is shown as returned.
 */
import api from '../../services/api';

export interface AtdResult<T> { success: boolean; data?: T; error?: string; code?: string; message?: string; detail?: unknown; status?: number }

export type AtdDateKind = 'ATD_DATE' | 'DISPENSE_DATE' | 'EFFECTIVE_DATE';
export interface AtdMove { to: string; date: AtdDateKind; reason_required: boolean; meaning: string }
export interface AtdActor { user_id: string; name: string | null; role: string | null }
export interface AtdHistoryRow {
  id: string; from: string | null; to: string; effective_date: string | null; source: 'NATIVE' | 'MONDAY_BASELINE';
  actor: AtdActor | null; note: string | null; at: string;
}
export interface AtdNotice {
  kind: 'PROVIDER' | 'SCHEDULING'; channel: 'EMAIL' | 'IN_APP'; outcome: 'SENT' | 'SINK' | 'SKIPPED' | 'FAILED';
  reason: string | null; recipients: number; at: string;
}
export type AtdPlanAttention = 'VISIT_OTHER_BUILDING' | 'VISIT_CANCELLED' | 'VISIT_COMPLETED' | 'VISIT_PASSED' | 'PIT_STOP_PASSED';
export interface AtdDispensePlan {
  state: 'NOT_PLANNED' | 'ON_MANIFEST' | 'PIT_STOP'; visit_on: string | null; visit_state: string | null; pit_stop_on: string | null; note: string | null; recorded_at: string | null;
  /** Why the plan no longer holds, judged by the server against the resident's current building and today. */
  attention?: AtdPlanAttention | null;
  recorded_state?: 'NOT_PLANNED' | 'ON_MANIFEST' | 'PIT_STOP';
}
/** `resend`: RESIDENT_ATD_MARK where the resident is, while their ATD is Yes. */
/** `all_residents` (GET /meta only): the caller may read "ATD — all residents" (RESIDENT_ATD_MARK). */
export interface AtdCan { mark: boolean; dispense: boolean; resend?: boolean; all_residents?: boolean }
export interface AtdResendOutcome {
  event_id: string; skipped: string | null; failed: boolean;
  /** PR #419 round 3: UNRECORDED_DELIVERY = sent, but eMerge could not record it; NOT_RECORDABLE = nothing more sent, nothing could be recorded. */
  halted?: 'UNRECORDED_DELIVERY' | 'NOT_RECORDABLE' | null;
  notices: { kind: 'PROVIDER' | 'SCHEDULING'; channel: 'EMAIL' | 'IN_APP'; outcome: 'SENT' | 'SINK' | 'SKIPPED' | 'FAILED'; reason: string | null; unrecorded?: boolean }[];
}
export interface AtdMeta { labels: string[]; meaning: Record<string, string>; writes: 'NATIVE' | 'NONE'; can: AtdCan; today: string }
/** A building closed for scheduling (2026-10-14): ATD is still marked there, but no manifest or pit stop is planned. */
export interface AtdClosure { kind: string | null; reason: string | null; words: string }
/**
 * Scheduling's standing after (or before) Mark ATD (2026-10-15): whether the resident is on Scheduling's ATD list
 * ("ATD — ready to dispense"), and whether their building is closed for scheduling.
 */
export interface AtdScheduling { in_atd_list: boolean; list: string; building_closed: AtdClosure | null; can_plan_manifest: boolean }
export interface AtdResident {
  patient_id: string; resident: { name: string | null; status: string | null };
  facility: { id: string; name: string | null; closure?: AtdClosure | null } | null;
  /** No confirmed building: the resident's CounselEAR Preferred Clinic (unconfirmed), for BuildingOrClinic. */
  counselear_clinic?: { id: string | null; name: string | null } | null;
  scheduling?: AtdScheduling;
  merged_into_patient_id: string | null; status: string | null; meaning: string | null; recognised: boolean; atd_date: string | null; today: string;
  allowed: AtdMove[]; allowed_for_you: AtdMove[]; can: AtdCan; history: AtdHistoryRow[]; notices: AtdNotice[];
  dispense_plan: AtdDispensePlan | null; source: 'EMERGE';
}
export interface AtdChangeBody { to_status: string; effective_date?: string; note?: string }
export interface AtdChangeOutcome {
  changed?: boolean; unchanged?: boolean; replayed?: boolean; status?: string | null; atd_date?: string | null; previous?: string | null; event?: unknown;
  /** The building the act landed on, and Scheduling's standing now (absent from a server before 2026-10-15). */
  building?: { id: string; name: string | null } | null;
  scheduling?: AtdScheduling;
}

/* ── "ATD — all residents" (GET /atd/residents) ──────────────────────── */

/** The ATD buckets the list is filtered and counted by: a closed vocabulary, the server's. */
export type AtdBucket = 'any' | 'yes' | 'dispensed' | 'none' | 'other';
export const ATD_BUCKETS: readonly AtdBucket[] = ['any', 'yes', 'dispensed', 'none', 'other'];

export interface AtdResidentRow {
  id: string;
  resident: { id: string; name: string; status: string | null };
  facility: { id: string; name: string | null } | null;
  atd: {
    /** The stored word as the command reads it (" yes " is Yes); null = blank, never "No". */
    status: string | null;
    bucket: Exclude<AtdBucket, 'any'>;
    recognised: boolean;
    meaning: string | null;
    atd_date: string | null;
    color: string | null;
    marker: import('../tide').MarkerShape | null;
    source: 'EMERGE';
  };
  /** Yes residents only: Scheduling's plan, judged by the server against the resident's building and today. */
  dispense_plan: (AtdDispensePlan & { visit_plan_id?: string | null }) | null;
}
export interface AtdResidentsTab {
  key: AtdBucket; label: string; source: 'EMERGE'; predicate: string; board: null; what: string;
  color: string | null; marker?: import('../tide').MarkerShape; bucket: boolean; count: number;
}
export interface AtdResidentsPage {
  board: 'atd-residents'; mode: 'EMERGE'; tab: AtdBucket; default_tab: AtdBucket; board_view: null; what: string; source: 'EMERGE';
  rule: string;
  tabs: AtdResidentsTab[];
  counts: Record<AtdBucket, number>;
  sort: string | null; dir: 'asc' | 'desc'; sorts: string[];
  filters: { q: string | null; building: string | null; status: AtdBucket };
  filter_options: { buildings: { id: string; name: string; count: number }[]; statuses: { id: AtdBucket; name: string; count: number }[] };
  items: AtdResidentRow[];
  total: number; page: number; limit: number; today: string; synced_at: null;
  writes: 'NATIVE' | 'NONE';
  can: AtdCan;
  scope: { enterprise: boolean; facility_count: number };
}
export interface AtdResidentsQuery { q?: string; building?: string; status?: string; sort?: string | null; dir?: 'asc' | 'desc'; page?: number; limit?: number }

export function atdResidentsQueryString(p: AtdResidentsQuery): string {
  const qp = new URLSearchParams();
  const put = (k: string, v: string | number | null | undefined) => { if (v !== null && v !== undefined && v !== '') qp.set(k, String(v)); };
  put('q', p.q);
  put('building', p.building);
  put('status', p.status && p.status !== 'any' ? p.status : null);
  put('sort', p.sort || null);
  put('dir', p.sort ? p.dir : null);
  put('page', p.page && p.page > 1 ? p.page : null);
  put('limit', p.limit);
  const s = qp.toString();
  return s ? `?${s}` : '';
}

/** A thrown transport error is "the server was never reached": no status, no code. */
async function call<T>(run: () => Promise<any>): Promise<AtdResult<T>> {
  try {
    const r = await run();
    return (r || { success: false, error: 'No answer from the server.' }) as AtdResult<T>;
  } catch {
    return { success: false, error: 'Could not reach the server.' };
  }
}

/** A fresh key per submit; a retry of the same submit reuses it. The server accepts 8-120 characters of [A-Za-z0-9:_-]. */
export const newAtdKey = (): string => {
  const raw = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `atd-${raw}`.replace(/[^A-Za-z0-9:_-]/g, '-').slice(0, 120);
};

export const atdApi = {
  meta: () => call<AtdMeta>(() => api.get('/atd/meta')),
  /** Every current resident in scope, one page (the "ATD — all residents" view). */
  residents: (p: AtdResidentsQuery = {}) => call<AtdResidentsPage>(() => api.get(`/atd/residents${atdResidentsQueryString(p)}`)),
  resident: (patientId: string) => call<AtdResident>(() => api.get(`/atd/residents/${encodeURIComponent(patientId)}`)),
  change: (patientId: string, body: AtdChangeBody, key: string) =>
    call<AtdChangeOutcome>(() => api.post(`/atd/residents/${encodeURIComponent(patientId)}`, body, { headers: { 'Idempotency-Key': key } })),
  /** Send what the current Yes's notices lack; nobody already told is told again (server-side idempotency). */
  resend: (patientId: string) => call<AtdResendOutcome>(() => api.post(`/atd/residents/${encodeURIComponent(patientId)}/notices/resend`, {})),
};
